import {
	Component,
	ComponentInterface,
	Element,
	Event,
	EventEmitter,
	h,
	Listen,
	Prop,
	State,
	Host,
} from '@stencil/core';
import {
	HoriztonalPosition,
	VerticalPosition,
} from '../../../../common/_types/positions';
import { Breakpoint, getCurrentBreakpoint } from '../../_helpers/breakpoints';

export type TooltipMode = '' | 'success' | 'caution' | 'error';
export type TooltipButtonType = 'icon' | 'primary-button';

const FadeInDelay = 100;
const FadeInDuration = 200;
const FadeOutDelay = 500;
const FadeOutDuration = 200;

@Component({
	tag: 'ds-tooltip',
})
export class Tooltip implements ComponentInterface {
	@State() isOpen = false;
	@State() transition: '' | 'fading-out' | 'fading-in' = '';
	@State() verticalPosition: VerticalPosition = VerticalPosition.Bottom;
	@State() horizontalPosition: HoriztonalPosition = HoriztonalPosition.Right;
	@Prop({ mutable: true }) openByDefault = false; // Note: only use this when visual regression testing with Storybook/Chromatic
	@Prop({ mutable: true }) header = '';
	@Prop({ mutable: true }) content = '';
	@Prop({ mutable: true }) ariaLabel: string = null;
	@Prop({ mutable: true }) mode: TooltipMode = '';
	@Prop({ mutable: true }) buttonType: TooltipButtonType = 'icon';
	@Prop({ mutable: true }) buttonColor:
		| 'white'
		| 'blue'
		| 'purple'
		| 'pink'
		| 'green'
		| 'yellow' = 'white';
	@Prop({ mutable: true }) buttonText = '';
	@Prop({ mutable: true }) buttonLinkEvent = '';
	@Prop({ reflect: true }) fauxDisabled = false; // If you want it to look like it is disabled but keep it clickable
	@Prop({ reflect: true }) disabled: boolean;

	@Event() tooltipOpen: EventEmitter<HTMLDsTooltipElement>;
	@Event() tooltipClosed: EventEmitter<HTMLDsTooltipElement>;

	@Element() el: HTMLDsTooltipElement;

	private _waitToOpen: number; // Timeout ref
	private _waitToClose: number; // Timeout ref

	@Listen('click')
	handleClick(event: MouseEvent): void {
		const targetClassList = (event.target as HTMLElement).classList;
		if (targetClassList.contains('fa-info-circle')) {
			this.toggle();
		}
	}

	@Listen('buttonClick')
	handleButtonClick(): void {
		this.toggle();
	}

	@Listen('click', { target: 'window' })
	handleClickOutSide(event: MouseEvent): void {
		if (
			this.isOpen &&
			!(event.target as HTMLElement).classList.contains('ds-tooltip-popover')
		) {
			this.close();
		}
	}

	@Listen('keyup')
	handleFocusinByTab(event): void {
		if (event.key === 'Tab') {
			this.toggleWithDelay();
		}
	}

	@Listen('keydown')
	handleFocusoutByTab(event): void {
		if (event.key === 'Tab') {
			this.toggleWithDelay();
		}
	}

	componentWillLoad(): Promise<void> | void {
		if (this.openByDefault) {
			this.open();
		}

		if (this.header) {
			this.header = this.handleNewLines(this.header);
		}

		if (this.content) {
			this.content = this.handleNewLines(this.content);
		}
	}

	/**
	 * Replaces all HTML BR tags and '\n' inside strings with actual line breaks
	 */
	handleNewLines(text: string): string {
		return text.replace(/<\/?(br) ?\/?>|\\n|&lt;\/br&gt;/gi, `\n`);
	}

	toggleWithDelay(): void {
		this.reset();
		if (this.isOpen) {
			this._waitToClose = window.setTimeout(() => this.close(), FadeOutDelay);
		} else {
			this._waitToOpen = window.setTimeout(() => this.open(), FadeInDelay);
		}
	}

	open(): void {
		this.setPopoverPosition();
		this.transition = 'fading-in';

		setTimeout(() => {
			this.transition = '';
			this.isOpen = true;
			this.tooltipOpen.emit(this.el);
		}, FadeInDuration);
	}

	close(): void {
		this.transition = 'fading-out';

		setTimeout(() => {
			this.transition = '';
			this.isOpen = false;
			this.tooltipClosed.emit(this.el);
		}, FadeOutDuration);
	}

	reset(): void {
		clearTimeout(this._waitToClose);
		clearTimeout(this._waitToOpen);
		this.transition = '';
	}

	/**
	 * This will catch events from all instances of tooltips as they open, enabling us to ensure only one
	 * tooltip can be open at a time.
	 * @param event
	 */
	@Listen('tooltipOpen', { target: 'window' })
	handleTooltipOpenEvent(event: CustomEvent<HTMLDsTooltipElement>): void {
		if (event.target !== this.el) {
			this.isOpen = false;
		}
	}

	@Listen('touchstart', { target: 'window' })
	handleWindowTouch(): void {
		if (this.isOpen) {
			this.close();
		}
	}

	@Listen('touchstart', { passive: false })
	handleComponentTouch(event: TouchEvent): void {
		event.preventDefault();
		this.toggle();
	}

	setPopoverPosition(): void {
		const positionRelToWindow = this.el.getBoundingClientRect();
		const windowWidth = window.innerWidth;
		const windowHeight = window.innerHeight;

		let centerAlignStartPos, centerAlignEndPos;
		if ([Breakpoint.XS, Breakpoint.SM].includes(getCurrentBreakpoint())) {
			centerAlignStartPos = (windowWidth / 5) * 2;
			centerAlignEndPos = (windowWidth / 5) * 3;
		} else {
			centerAlignStartPos = windowWidth / 4;
			centerAlignEndPos = (windowWidth / 4) * 3;
		}

		// For primary-button tooltips, center alignment doesn't work well, so disable it by setting centerAlignStartPos to centerAlignEndPos
		if (this.buttonType === 'primary-button') {
			centerAlignStartPos = centerAlignEndPos;
		}

		if (positionRelToWindow.left < centerAlignStartPos) {
			this.horizontalPosition = HoriztonalPosition.Right;
		} else if (positionRelToWindow.left < centerAlignEndPos) {
			this.horizontalPosition = HoriztonalPosition.Center;
		} else {
			this.horizontalPosition = HoriztonalPosition.Left;
		}

		if (positionRelToWindow.top < windowHeight / 3) {
			this.verticalPosition = VerticalPosition.Bottom;
		} else {
			this.verticalPosition = VerticalPosition.Top;
		}
	}

	getPopoverClasses(): { [key: string]: boolean } {
		const classes = {
			'ds-tooltip-popover': true,
			'ds-wider-popover': this.content.length > 25,
			'ds-body-s': true,
			[`ds-vposition-${this.verticalPosition}`]: true,
			[`ds-hposition-${this.horizontalPosition}`]: true,
			'ds-open': this.isOpen,
			'ds-fading-out': this.transition === 'fading-out',
			'ds-fading-in': this.transition === 'fading-in',
			'ds-mode-success': this.mode === 'success',
			'ds-mode-caution': this.mode === 'caution',
			'ds-mode-error': this.mode === 'error',
		};

		return classes;
	}

	toggle(): void {
		if (!this.isOpen) {
			this.open();
		} else {
			this.close();
		}
	}

	renderHeader() {
		let icon;
		switch (this.mode) {
			case 'caution':
				icon = <i class="fa fa-exclamation-triangle" aria-hidden="true" />;
				break;
			case 'success':
				icon = <i class="fa fa-check-circle" aria-hidden="true" />;
				break;
			case 'error':
				icon = <i class="fa fa-times-circle" aria-hidden="true" />;
		}

		return (
			<h5 class="ds-tooltip-header ds-body-xs-bold">
				{icon}
				{this.header}
			</h5>
		);
	}

	render() {
		return (
			<Host class="ds-tooltip-base" role="none">
				{[
					this.buttonType === 'icon' ? (
						<span
							class="ds-tooltip-icon"
							role="tooltip"
							aria-label={this.content}
							tabIndex={0}
						>
							<i class="fa fa-info-circle" />
						</span>
					) : (
						<ds-button
							class="ds-tooltip-toggle-button"
							data-link-event={this.buttonLinkEvent}
							type="primary"
							compact={true}
							color={this.buttonColor}
							show-as-active={this.isOpen}
							aria-label={this.ariaLabel ? this.ariaLabel : this.content}
							accessibility-role="tooltip"
							disabled={this.disabled}
							fauxDisabled={this.fauxDisabled}
						>
							{this.buttonText}
						</ds-button>
					),

					<div aria-hidden={true} class={this.getPopoverClasses()}>
						{this.isOpen
							? [
									this.header ? this.renderHeader() : '',
									<p innerHTML={this.content}></p>,
							  ]
							: ''}
					</div>,
				]}
			</Host>
		);
	}
}
